// BM-H4056 - turns a submitted LPA questionnaire into a readable summary for staff.
//
// The staff review page (FeeEarnerFormReview.jsx) used to print the client's answers with
// JSON.stringify: keys such as lpa_kind "pf", internal ids and true/false flags. In UAT
// (8 Oct 2026, Script 1 Part C step 2) the tester could not compare it against a test data
// sheet, so the step failed. This builds the same answers as labelled rows, in the order of
// the client questionnaire (LPAFormSections.jsx), with UK dates and Yes/No.
//
// Nothing the client entered may be dropped from view. Every key the builder does not know
// about lands under "Other answers" with a readable label, so a new question added to the
// questionnaire shows up here (plainly labelled) rather than vanishing.
//
// Pure functions only, no React, so the dry run over live forms can run the same code in Node.

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/;

// "1961-03-14" -> "14 March 1961". Read off the string itself rather than through Date,
// so a date of birth cannot slip a day across a time zone.
export function ukDate(value) {
  if (typeof value !== "string") return value;
  const m = ISO_DATE.exec(value.trim());
  if (!m) return value;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return value;
  return `${Number(m[3])} ${month} ${m[1]}`;
}

export function yesNo(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return value;
}

function isBlank(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

// "has_will_in_place" -> "Has will in place"
export function humanise(key) {
  const words = String(key).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!words) return String(key);
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function formatAddress(address) {
  if (!address) return "";
  if (typeof address === "string") return address;
  const lines = Array.isArray(address.lines) ? address.lines : [];
  return [...lines, address.postcode].filter((p) => !isBlank(p)).map((p) => String(p).trim()).join(", ");
}

export function personName(person) {
  if (!person) return "";
  if (typeof person === "string") return person;
  return [person.title, person.first_names, person.last_name]
    .filter((p) => !isBlank(p))
    .map((p) => String(p).trim())
    .join(" ");
}

function isAddressShape(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    && ("lines" in value || "postcode" in value)
    && Object.keys(value).every((k) => k === "lines" || k === "postcode");
}

// Keys that are machine bookkeeping, never something the client typed.
const INTERNAL_KEYS = new Set(["id"]);

// One readable string for any scalar; nested values are flattened by genericRows.
export function plainValue(value) {
  if (value === true || value === false) return yesNo(value);
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return ukDate(value);
  if (isAddressShape(value)) return formatAddress(value);
  if (Array.isArray(value) && value.every((v) => v === null || typeof v !== "object")) {
    return value.filter((v) => !isBlank(v)).map(plainValue).join(", ");
  }
  return null;
}

// Flattens anything into [{label, value}] rows with readable labels. Used for keys the
// builder does not know, and as the whole view for form kinds with no tailored summary.
export function genericRows(value, label = "") {
  if (isBlank(value)) return [];
  const flat = plainValue(value);
  if (flat !== null) return [{ label: label || "Answer", value: flat }];
  if (Array.isArray(value)) {
    return value.flatMap((item, i) => genericRows(item, `${label ? `${label} ` : ""}${i + 1}`.trim()));
  }
  return Object.entries(value)
    .filter(([k]) => !INTERNAL_KEYS.has(k))
    .flatMap(([k, v]) => genericRows(v, label ? `${label}: ${humanise(k).toLowerCase()}` : humanise(k)));
}

const LPA_KIND_LABELS = {
  pf: "Property and financial affairs (form LP1F)",
  hw: "Health and welfare (form LP1H)",
};

// Wording follows LPAFormSections.jsx, with the legal term first so it matches what a fee
// earner writes on a data sheet ("jointly and severally").
const DECISION_MODE_LABELS = {
  sole: "Sole attorney: one attorney acts alone",
  jointly: "Jointly: all attorneys must agree on every decision",
  jointly_and_severally: "Jointly and severally: attorneys may act together or separately",
  mixed: "Jointly for some decisions, jointly and severally for others",
};

const REPLACEMENT_MODE_LABELS = {
  default_all_at_once: "All replacements step in at the same time",
  custom: "Something else (described below)",
};

const WHEN_ACT_LABELS = {
  only_on_incapacity: "Only when the donor does not have mental capacity to make the decision",
  on_registration_and_incapacity: "As soon as the LPA is registered (and also if the donor later loses capacity)",
};

const LIFE_SUSTAINING_LABELS = {
  option_a: "Option A: attorneys CAN give or refuse consent to life-sustaining treatment",
  option_b: "Option B: attorneys CANNOT give or refuse consent to life-sustaining treatment",
};

const CERT_BASIS_LABELS = {
  known_2_years: "Has known the donor personally for at least 2 years",
  professional_skills: "Has relevant professional skills",
};

const CERT_EXCLUSION_LABELS = {
  is_attorney_this_lpa: "An attorney on this LPA?",
  is_attorney_other_lpa_or_epa_for_donor: "An attorney on another LPA or EPA for this donor?",
  is_replacement_this_lpa: "A replacement attorney on this LPA?",
  is_family_of_donor: "A family member of the donor?",
  is_family_of_attorney: "A family member of an attorney?",
  is_unmarried_partner_of_donor: "The donor's unmarried partner?",
  is_unmarried_partner_of_attorney: "An attorney's unmarried partner?",
  is_business_partner_of_donor_or_attorney: "A business partner of the donor or an attorney?",
  is_employee_of_donor_or_attorney: "An employee of the donor or an attorney?",
  is_care_home_stakeholder: "Owns, manages or works at the donor's care home?",
};

const CAPACITY_FLAG_LABELS = {
  medical_diagnosis_disclosed: "Medical diagnosis relevant to capacity disclosed?",
  family_dispute_indicator: "Family dispute that could affect this LPA?",
  communication_impairment: "Interpreter, hearing impairment or language barrier?",
  age_over_85_no_recent_medical: "Over 85 with no recent medical assessment on file?",
  undue_pressure_indicator: "Any sign of undue pressure during the capacity conversation?",
  capacity_challenge_history: "Earlier LPA rejected or revoked on capacity grounds, or Court of Protection involved?",
};

const CAPACITY_REVIEW_LABELS = {
  understands_information: "Can understand the information relevant to making an LPA",
  retains_information: "Can retain the information long enough to decide",
  weighs_information: "Can use or weigh the information",
  communicates_decision: "Can communicate the decision",
  free_from_pressure: "Is deciding free from undue pressure",
  notes: "Notes for the certificate provider",
};

const FEE_ROUTE_LABELS = {
  full: "Pay the full fee",
  remission: "Fee remission (reduced fee, income based)",
  exemption: "Fee exemption (means-tested benefits)",
  hardship: "Financial hardship",
  repeat: "Repeat application",
};

const FEE_FIELD_LABELS = {
  evidence_declared: "Evidence provided?",
  donor_annual_gross_income: "Donor's annual gross income",
  hardship_letter_included: "Hardship letter included?",
  repeat_case_number: "Case number of the earlier application",
  prior_refusal_date: "Date of the earlier refusal",
};

const NOT_ANSWERED = "Not answered";

function choice(map, value) {
  if (isBlank(value)) return NOT_ANSWERED;
  return map[value] || humanise(value);
}

// Rows for the fields of an object that a section did not label itself, so nothing is lost.
function leftoverRows(obj, knownKeys) {
  if (!obj || typeof obj !== "object") return [];
  const rows = [];
  Object.entries(obj).forEach(([k, v]) => {
    if (knownKeys.includes(k) || INTERNAL_KEYS.has(k)) return;
    rows.push(...genericRows(v, humanise(k)));
  });
  return rows;
}

function row(label, value, { always = false } = {}) {
  if (isBlank(value)) return always ? [{ label, value: NOT_ANSWERED }] : [];
  return [{ label, value: String(value) }];
}

const PERSON_KEYS = ["title", "first_names", "last_name", "other_names", "date_of_birth", "address", "email", "phone"];

function personRows(person, extraKnown = []) {
  const p = person || {};
  return [
    ...row("Name", personName(p), { always: true }),
    ...row("Other names known by", p.other_names),
    ...row("Date of birth", ukDate(p.date_of_birth)),
    ...row("Address", formatAddress(p.address)),
    ...row("Email", p.email),
    ...row("Phone", p.phone),
    ...leftoverRows(p, [...PERSON_KEYS, ...extraKnown]),
  ];
}

function peopleCards(list, noun) {
  return (Array.isArray(list) ? list : []).map((person, i) => ({
    heading: `${noun} ${i + 1}: ${personName(person) || "name not given"}`,
    rows: personRows(person, ["bankruptcy_status", "capacity_declared"]).concat(
      row("Bankrupt or subject to a debt relief order?", yesNo(person?.bankruptcy_status)),
      row("Has declared capacity to act?", yesNo(person?.capacity_declared)),
    ),
  }));
}

function yesNoList(obj, labels) {
  const o = obj || {};
  const rows = Object.entries(labels).map(([key, label]) => ({
    label,
    value: isBlank(o[key]) ? NOT_ANSWERED : String(yesNo(o[key])),
  }));
  return rows.concat(leftoverRows(o, Object.keys(labels)));
}

// Instrument keys each section below reads. Anything else is shown under "Other answers".
const KNOWN_INSTRUMENT_KEYS = [
  "lpa_kind", "lpa_type", "donor", "s2_replacement", "attorneys", "decision_mode",
  "replacements", "replacement_mode", "when_attorneys_can_act", "life_sustaining_treatment",
  "preferences_and_instructions", "notified_persons", "cert_provider", "applicant",
  "correspondent", "fee_route", "capacity_review",
];

export function buildLpaSummary(answers) {
  const all = answers || {};
  const ins = all.instrument || {};
  const kind = ins.lpa_kind || ins.lpa_type;
  const attorneys = Array.isArray(ins.attorneys) ? ins.attorneys : [];
  const attorneyName = (id) => personName(attorneys.find((a) => a && a.id === id)) || "an attorney not on the list";
  const sections = [];

  sections.push({
    id: "lpa_type",
    title: "Type of LPA",
    rows: [
      { label: "Type", value: kind ? (LPA_KIND_LABELS[kind] || humanise(kind)) : NOT_ANSWERED },
      // Both keys are written from the same choice; show the second only if it disagrees.
      ...(ins.lpa_kind && ins.lpa_type && ins.lpa_kind !== ins.lpa_type
        ? [{ label: "Type (second record, differs)", value: LPA_KIND_LABELS[ins.lpa_type] || humanise(ins.lpa_type) }]
        : []),
    ],
  });

  const donor = ins.donor || {};
  sections.push({
    id: "s1_donor",
    title: "1. The donor",
    rows: [
      ...personRows(donor, ["welsh_correspondence", "has_will_in_place", "capacity_flags"]),
      ...row("OPG to correspond in Welsh?", yesNo(donor.welsh_correspondence)),
      ...row("Has a Will in place?", yesNo(donor.has_will_in_place)),
    ],
  });

  sections.push({
    id: "s2_replacement",
    title: "2. Replacing an earlier LPA or EPA",
    rows: row("Notes about any earlier power of attorney", ins.s2_replacement, { always: true }),
  });

  const decision = ins.decision_mode || {};
  sections.push({
    id: "s3_attorneys",
    title: "3. The attorneys",
    rows: [
      ...row("How they make decisions", choice(DECISION_MODE_LABELS, decision.type), { always: attorneys.length > 1 }),
      ...row("Decisions that must be made jointly", decision.joint_decisions),
      ...leftoverRows(decision, ["type", "joint_decisions"]).map((r) => ({ ...r, label: `How they make decisions: ${r.label.toLowerCase()}` })),
    ],
    people: peopleCards(attorneys, "Attorney"),
    emptyPeople: attorneys.length === 0 ? "No attorneys named" : null,
  });

  const replacements = Array.isArray(ins.replacements) ? ins.replacements : [];
  const repMode = ins.replacement_mode || {};
  sections.push({
    id: "s4_replacements",
    title: "4. Replacement attorneys",
    rows: replacements.length > 0
      ? [
        ...row("How replacements step in", choice(REPLACEMENT_MODE_LABELS, repMode.type), { always: true }),
        ...row("Description", repMode.custom_specification),
      ]
      : [],
    people: peopleCards(replacements, "Replacement attorney"),
    emptyPeople: replacements.length === 0 ? "No replacement attorneys named" : null,
  });

  const lst = ins.life_sustaining_treatment || {};
  sections.push({
    id: "s5_when_act",
    title: kind === "hw" ? "5. Life-sustaining treatment" : "5. When the attorneys can act",
    rows: kind === "hw"
      ? [
        ...row("Life-sustaining treatment", choice(LIFE_SUSTAINING_LABELS, lst.choice), { always: true }),
        ...leftoverRows(lst, ["choice"]),
        ...row("When the attorneys can act", ins.when_attorneys_can_act && choice(WHEN_ACT_LABELS, ins.when_attorneys_can_act)),
      ]
      : [
        ...row("When the attorneys can act", choice(WHEN_ACT_LABELS, ins.when_attorneys_can_act), { always: true }),
        ...(isBlank(ins.life_sustaining_treatment) ? [] : genericRows(ins.life_sustaining_treatment, "Life-sustaining treatment")),
      ],
  });

  const pi = ins.preferences_and_instructions || {};
  sections.push({
    id: "s6_preferences",
    title: "6. Preferences",
    rows: row("Preferences", pi.preferences, { always: true }),
  });
  sections.push({
    id: "s7_instructions",
    title: "7. Instructions",
    rows: [
      ...row("Instructions", pi.instructions, { always: true }),
      ...leftoverRows(pi, ["preferences", "instructions"]),
    ],
  });

  const notified = Array.isArray(ins.notified_persons) ? ins.notified_persons : [];
  sections.push({
    id: "s8_notified",
    title: "8. People to tell",
    rows: [],
    people: notified.map((p, i) => ({
      heading: `Person to tell ${i + 1}: ${personName(p) || "name not given"}`,
      rows: personRows(p),
    })),
    emptyPeople: notified.length === 0 ? "Nobody named" : null,
  });

  const cp = ins.cert_provider || {};
  sections.push({
    id: "s9_certificate",
    title: "9. Certificate provider",
    rows: [
      ...personRows(cp, ["basis", "exclusions", "days_to_availability"]),
      ...row("Basis", choice(CERT_BASIS_LABELS, cp.basis), { always: true }),
      ...row("Days until available to act", cp.days_to_availability),
    ],
    people: [
      { heading: "Is the certificate provider...", rows: yesNoList(cp.exclusions, CERT_EXCLUSION_LABELS) },
      { heading: "Before certification (donor)", rows: yesNoList(donor.capacity_flags, CAPACITY_FLAG_LABELS) },
      ...(isBlank(all.capacity_review) && isBlank(ins.capacity_review) ? [] : [{
        heading: "Capacity conversation",
        rows: yesNoList({ ...(ins.capacity_review || {}), ...(all.capacity_review || {}) }, CAPACITY_REVIEW_LABELS)
          // "yes" / "no" / "unsure" read as words; free text (the notes) is left as typed.
          .map((r) => ({ ...r, value: ["yes", "no", "unsure"].includes(r.value) ? humanise(r.value) : r.value })),
      }]),
    ],
  });

  const applicant = ins.applicant || {};
  const correspondent = ins.correspondent || {};
  const prefs = correspondent.contact_prefs || {};
  const fee = ins.fee_route || {};
  const contactBy = [prefs.post && "Post", prefs.phone && `Phone${prefs.phone_number ? ` (${prefs.phone_number})` : ""}`,
    prefs.email && `Email${prefs.email_address ? ` (${prefs.email_address})` : ""}`].filter(Boolean).join(", ");
  let applicantText = NOT_ANSWERED;
  if (applicant.type === "donor") applicantText = "The donor";
  else if (applicant.type === "attorneys") applicantText = "The attorneys";
  else if (applicant.type) applicantText = humanise(applicant.type);
  let correspondentText = NOT_ANSWERED;
  if (correspondent.type === "donor") correspondentText = "The donor";
  else if (correspondent.type === "attorney") correspondentText = `An attorney: ${attorneyName(correspondent.attorney_id)}`;
  else if (correspondent.type === "other") correspondentText = `Someone else: ${personName(correspondent) || "name not given"}`;
  else if (correspondent.type) correspondentText = humanise(correspondent.type);

  sections.push({
    id: "s10_review",
    title: "10. Registration",
    rows: [
      { label: "Who is applying to register", value: applicantText },
      ...row("Attorneys signing the application",
        (applicant.attorney_signatures_required || []).map(attorneyName).join(", ")),
      ...leftoverRows(applicant, ["type", "attorney_signatures_required"]),
      { label: "Who OPG should contact", value: correspondentText },
      ...(correspondent.type === "other" ? [
        ...row("Company", correspondent.company),
        ...row("Address", formatAddress(correspondent.address)),
      ] : []),
      ...row("Contact by", contactBy),
      ...row("Correspond in Welsh?", prefs.welsh ? "Yes" : null),
      ...leftoverRows(correspondent, ["type", "attorney_id", "contact_prefs", "title", "first_names", "last_name", "company", "address"]),
      ...leftoverRows(prefs, ["post", "phone", "phone_number", "email", "email_address", "welsh"]),
      { label: "Registration fee", value: choice(FEE_ROUTE_LABELS, fee.type) },
      ...Object.entries(FEE_FIELD_LABELS).flatMap(([k, label]) =>
        row(label, k === "prior_refusal_date" ? ukDate(fee[k]) : yesNo(fee[k]))),
      ...leftoverRows(fee, ["type", ...Object.keys(FEE_FIELD_LABELS)]),
    ],
  });

  // Everything else the client sent, inside the instrument or beside it, readably labelled.
  const otherRows = [
    ...leftoverRows(ins, KNOWN_INSTRUMENT_KEYS),
    ...leftoverRows(all, ["instrument", "capacity_review"]),
  ];
  if (otherRows.length > 0) {
    sections.push({ id: "other", title: "Other answers", rows: otherRows });
  }

  return sections;
}

// Any form kind without a tailored summary: one section of readable rows.
export function buildGenericSummary(answers) {
  const rows = genericRows(answers || {});
  return [{ id: "answers", title: "Answers", rows }];
}
